// FEATURE 03/07: editar uma venda já feita (Vendas→Pedidos, PDV Vendas do dia, Caixa).
// Profissional: Observação é LIVRE; Cliente/Vendedor exigem SENHA de gerente.
// Toda edição gera evento no histórico do pedido (quem/quando/de→para). Itens/valores
// NÃO são editáveis aqui (mexem em estoque/financeiro/fiscal). Backend valida tudo.
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Check, Loader2, Search, History, Pencil, User, ShieldAlert, Package, ArrowRightLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

function fmtData(s?: string | null) {
  if (!s) return ''
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z'  // backend grava UTC naïve
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

// Rótulo amigável dos tipos de evento pra timeline.
const TIPO_LABEL: Record<string, string> = {
  edicao: 'Edição', aprovacao: 'Aprovação', avanco_etapa: 'Avanço de etapa',
  faturamento: 'Faturamento', entrega: 'Entrega', bloqueio: 'Bloqueio',
  pagamento_confirmado: 'Pagamento confirmado', pagamento_desfeito: 'Pagamento desfeito',
  inicio_separacao: 'Início separação', conclusao_separacao: 'Separação concluída',
  reprovacao_pagamento: 'Reprovação', cancelamento: 'Cancelamento',
}

interface Props {
  pedidoId: number
  abaInicial?: 'editar' | 'historico'
  onClose: () => void
  onSaved?: () => void
}

export default function EditarVendaModal({ pedidoId, abaInicial = 'editar', onClose, onSaved }: Props) {
  const qc = useQueryClient()
  const [aba, setAba] = useState<'editar' | 'historico'>(abaInicial)

  const { data: atual, isLoading } = useQuery<any>({
    queryKey: ['venda-editar', pedidoId],
    queryFn: async () => (await api.get(`/vendas/pedidos/${pedidoId}/editar-campos`)).data,
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between shrink-0">
          <h2 className="text-lg font-semibold">
            Venda {atual?.numero ? `#${atual.numero}` : ''}
          </h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X size={18} /></button>
        </div>

        <div className="flex border-b border-ink-200 shrink-0">
          <button onClick={() => setAba('editar')}
            className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-1.5 ${
              aba === 'editar' ? 'text-brand-700 border-b-2 border-brand-600' : 'text-ink-500 hover:text-ink-700'}`}>
            <Pencil size={14} /> Editar
          </button>
          <button onClick={() => setAba('historico')}
            className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-1.5 ${
              aba === 'historico' ? 'text-brand-700 border-b-2 border-brand-600' : 'text-ink-500 hover:text-ink-700'}`}>
            <History size={14} /> Histórico
          </button>
        </div>

        <div className="p-4 overflow-auto">
          {isLoading ? (
            <div className="text-ink-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carregando…</div>
          ) : aba === 'editar' ? (
            <FormEditar atual={atual} pedidoId={pedidoId}
              onDone={() => {
                qc.invalidateQueries({ queryKey: ['venda-editar', pedidoId] })
                qc.invalidateQueries({ queryKey: ['venda-historico', pedidoId] })
                onSaved?.()
              }} />
          ) : (
            <Historico pedidoId={pedidoId} />
          )}
        </div>
      </div>
    </div>
  )
}

function FormEditar({ atual, pedidoId, onDone }: { atual: any; pedidoId: number; onDone: () => void }) {
  const [observacao, setObservacao] = useState<string>(atual?.observacao || '')
  const [cliente, setCliente] = useState<{ id: number; nome: string } | null>(atual?.cliente || null)
  const [vendId, setVendId] = useState<number | ''>(atual?.vendedor?.id ?? '')
  const [senha, setSenha] = useState('')
  const [buscaCli, setBuscaCli] = useState('')

  // NOVO — troca de produto: mapa { item_id: novo_produto_id | null }
  const [trocaItem, setTrocaItem] = useState<Record<number, { produto_id: number; descricao: string; preco_venda: number } | null>>({})
  const [buscaProdItem, setBuscaProdItem] = useState<Record<number, string>>({})
  const [abrirBuscaItem, setAbrirBuscaItem] = useState<number | null>(null)

  const obsMudou = (observacao.trim() || '') !== ((atual?.observacao || '').trim())
  const cliMudou = (cliente?.id ?? null) !== (atual?.cliente?.id ?? null)
  const vendMudou = (vendId === '' ? null : vendId) !== (atual?.vendedor?.id ?? null)
  const itensParaTrocar = Object.entries(trocaItem).filter(([, v]) => v != null)
  const produtoMudou = itensParaTrocar.length > 0
  const precisaSenha = cliMudou || vendMudou || produtoMudou
  const temMudanca = obsMudou || cliMudou || vendMudou || produtoMudou

  const { data: resCli = [] } = useQuery<any[]>({
    queryKey: ['edit-cli-busca', buscaCli],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { q: buscaCli, tipo: 'cliente', limit: 15 } })).data,
    enabled: buscaCli.trim().length >= 2,
  })
  // FIX (relatado 10/09): faltava filtrar por filial — trazia vendedor de TODAS
  // as lojas. Usa a filial do próprio pedido (não a do usuário logado), pra
  // editar uma venda de outra loja continuar mostrando os vendedores certos.
  const { data: vendedores = [] } = useQuery<any[]>({
    queryKey: ['edit-vendedores', atual?.filial_id],
    queryFn: async () => (await api.get('/cadastros/colaboradores', {
      params: { tipo: 'vendedor', ativos: true, filial: true, filial_id: atual?.filial_id ?? undefined },
    })).data,
    enabled: !!atual,
  })

  // NOVO — busca de produto por item (uma query por item aberto, reaproveitando a mesma chave de busca)
  const buscaAtiva = abrirBuscaItem != null ? (buscaProdItem[abrirBuscaItem] || '') : ''
  const { data: produtosBusca = [] } = useQuery<any[]>({
    queryKey: ['edit-produtos-busca', buscaAtiva],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { q: buscaAtiva.trim() || undefined, limit: 20 } })).data,
    enabled: abrirBuscaItem != null && buscaAtiva.trim().length > 0,
  })

  const salvar = useMutation({
    mutationFn: async () => {
      const body: any = {}
      if (obsMudou) body.observacao = observacao.trim() || null
      if (cliMudou) body.cliente_id = cliente?.id ?? null
      if (vendMudou) body.vendedor_cad_id = vendId === '' ? null : vendId
      if (produtoMudou) {
        body.itens_trocar_produto = itensParaTrocar.map(([itemId, v]) => ({
          item_id: Number(itemId), novo_produto_id: v!.produto_id,
        }))
      }
      if (precisaSenha) body.senha = senha
      return (await api.patch(`/vendas/pedidos/${pedidoId}/editar-campos`, body)).data
    },
    onSuccess: () => { toast.success('Venda atualizada'); onDone() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  return (
    <div className="space-y-4 text-sm">
      {/* Observação — LIVRE */}
      <label className="block">
        <div className="text-xs font-medium mb-1 flex items-center gap-1">Observação <span className="text-[10px] text-emerald-600 font-normal">· livre</span></div>
        <textarea value={observacao} onChange={e => setObservacao(e.target.value.toUpperCase())}
          rows={3} className="input w-full" placeholder="Ex.: pago no balcão, retirada amanhã…" />
      </label>

      {/* Cliente — SENSÍVEL */}
      <div>
        <div className="text-xs font-medium mb-1 flex items-center gap-1">
          <User size={12} /> Cliente <span className="text-[10px] text-amber-600 font-normal">· exige senha</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex-1 truncate rounded-lg bg-ink-50 border border-ink-200 px-3 py-2">
            {cliente?.nome || <span className="text-ink-400">Sem cliente</span>}
          </span>
        </div>
        <div className="relative mt-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
          <input value={buscaCli} onChange={e => setBuscaCli(e.target.value)}
            className="input w-full pl-8 text-sm" placeholder="Trocar cliente (digite p/ buscar)…" />
          {buscaCli.trim().length >= 2 && resCli.length > 0 && (
            <div className="absolute z-10 mt-1 w-full max-h-44 overflow-auto bg-white border border-ink-200 rounded-lg shadow-lg">
              {resCli.map(p => (
                <button key={p.id} onClick={() => { setCliente({ id: p.id, nome: p.nome }); setBuscaCli('') }}
                  className="w-full text-left px-3 py-1.5 hover:bg-brand-50 text-sm truncate">{p.nome}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Vendedor — SENSÍVEL */}
      <label className="block">
        <div className="text-xs font-medium mb-1">Vendedor <span className="text-[10px] text-amber-600 font-normal">· exige senha</span></div>
        <select value={vendId} onChange={e => setVendId(e.target.value === '' ? '' : Number(e.target.value))}
          className="input w-full">
          <option value="">Sem vendedor</option>
          {vendedores.map(v => <option key={v.id} value={v.id}>{v.nome}</option>)}
        </select>
      </label>

      {/* Produtos dos itens — SENSÍVEL — NOVO */}
      <div>
        <div className="text-xs font-medium mb-1 flex items-center gap-1">
          <Package size={12} /> Produtos da venda <span className="text-[10px] text-amber-600 font-normal">· exige senha</span>
        </div>
        {atual?.tem_nf ? (
          <div className="text-xs text-ink-500 bg-ink-50 border border-ink-200 rounded-lg p-2.5">
            Esta venda já tem NF-e emitida — não é possível trocar o produto por aqui.
            Cancele ou corrija a nota fiscal primeiro.
          </div>
        ) : (
          <div className="space-y-2">
            {(atual?.itens || []).map((it: any) => {
              const troca = trocaItem[it.item_id]
              const buscaVal = buscaProdItem[it.item_id] || ''
              return (
                <div key={it.item_id} className="rounded-lg border border-ink-200 p-2 relative">
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className={troca ? 'line-through text-ink-400' : 'text-ink-700 font-medium'}>
                      {it.descricao}
                    </span>
                    <span className="text-ink-400 shrink-0">Qtd {it.quantidade}</span>
                  </div>
                  {troca && (
                    <div className="flex items-center gap-1.5 text-xs text-emerald-700 font-medium mt-1">
                      <ArrowRightLeft size={12} /> {troca.descricao}
                    </div>
                  )}
                  <div className="relative mt-1.5">
                    <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-400" />
                    <input
                      value={buscaVal}
                      autoComplete="off"
                      onChange={e => {
                        setBuscaProdItem({ ...buscaProdItem, [it.item_id]: e.target.value })
                        setAbrirBuscaItem(it.item_id)
                      }}
                      onFocus={() => setAbrirBuscaItem(it.item_id)}
                      className="input w-full pl-7 py-1 text-xs"
                      placeholder="Trocar por outro produto (digite p/ buscar)…"
                    />
                    {troca && (
                      <button type="button"
                        onClick={() => {
                          const novo = { ...trocaItem }; delete novo[it.item_id]
                          setTrocaItem(novo)
                          setBuscaProdItem({ ...buscaProdItem, [it.item_id]: '' })
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-rose-600">
                        <X size={13} />
                      </button>
                    )}
                    {abrirBuscaItem === it.item_id && buscaVal.trim() && (
                      <div className="absolute z-20 mt-1 w-full max-h-48 overflow-auto bg-white border border-ink-200 rounded-lg shadow-lg">
                        {produtosBusca.length === 0 ? (
                          <div className="p-2 text-center text-xs text-ink-400">Nenhum produto encontrado</div>
                        ) : (
                          produtosBusca.map((p: any) => (
                            <button key={p.id} type="button"
                              onClick={() => {
                                setTrocaItem({ ...trocaItem, [it.item_id]: { produto_id: p.id, descricao: p.descricao, preco_venda: Number(p.preco_venda || 0) } })
                                setBuscaProdItem({ ...buscaProdItem, [it.item_id]: '' })
                                setAbrirBuscaItem(null)
                              }}
                              className="w-full text-left px-2.5 py-1.5 hover:bg-brand-50 text-xs border-b last:border-0 flex items-center justify-between gap-2">
                              <span className="truncate" title={p.descricao}>{p.descricao}</span>
                              <span className="text-emerald-700 font-medium shrink-0">R$ {Number(p.preco_venda || 0).toFixed(2)}</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Senha (quando mexe em cliente/vendedor/produto) */}
      {precisaSenha && (
        <label className="block rounded-lg border border-amber-300 bg-amber-50 p-3">
          <div className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1">
            <ShieldAlert size={13} /> Senha de gerente (admin/gerente/financeiro)
          </div>
          <input type="password" value={senha} onChange={e => setSenha(e.target.value)} autoComplete="new-password"
            className="input w-full" placeholder="Senha para autorizar a alteração" />
        </label>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button className="btn-primary" disabled={!temMudanca || salvar.isPending || (precisaSenha && !senha)}
          onClick={() => salvar.mutate()}>
          {salvar.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar
        </button>
      </div>
    </div>
  )
}

function Historico({ pedidoId }: { pedidoId: number }) {
  const { data: eventos = [], isLoading } = useQuery<any[]>({
    queryKey: ['venda-historico', pedidoId],
    queryFn: async () => (await api.get(`/vendas/pedidos/${pedidoId}/eventos`)).data,
  })
  if (isLoading) return <div className="text-ink-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carregando…</div>
  if (eventos.length === 0) return <div className="text-ink-400 text-sm">Sem histórico ainda.</div>
  return (
    <div className="space-y-2">
      {[...eventos].reverse().map(e => (
        <div key={e.id} className="rounded-lg border border-ink-200 p-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-ink-800">{TIPO_LABEL[e.tipo] || e.tipo}</span>
            <span className="text-[11px] text-ink-400">{fmtData(e.criado_em)}</span>
          </div>
          <div className="text-[11px] text-ink-500 mt-0.5">
            por {e.usuario_nome || 'sistema'}
          </div>
          {/* Detalhe das edições (campo: de → para) */}
          {Array.isArray(e.dados?.mudancas) && e.dados.mudancas.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {e.dados.mudancas.map((m: any, i: number) => (
                <li key={i} className="text-xs text-ink-700">
                  <strong>{m.campo}:</strong>{' '}
                  <span className="text-ink-400 line-through">{m.de ?? '—'}</span>{' → '}
                  <span className="text-emerald-700">{m.para ?? '—'}</span>
                </li>
              ))}
            </ul>
          )}
          {e.observacao && <div className="text-xs text-ink-500 mt-1 italic">“{e.observacao}”</div>}
        </div>
      ))}
    </div>
  )
}
